/**
 * WebSocket service for RAG (Retrieval Augmented Generation) chat with 3M Knowledge
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { IPublicClientApplication } from '@azure/msal-browser';
import logger from './logger';

export interface RagChatMessage {
  action: 'chat_message';
  message: string;
  messageId: string;
  timestamp: string;
  userId: string;
  sessionId: string;
}

export interface RagResponseChunk {
  type: 'chat_response_start' | 'chat_response_chunk' | 'chat_response_end' | 'chat_error' | 'chat_thinking';
  message_id?: string;
  original_message_id?: string;
  content?: string;
  error?: string;
  step?: string;
}

export class RagChatService {
  private websocket: WebSocket | null = null;
  private sessionId: string;
  private connectionId: string; // Unique identifier for this connection instance
  private msalInstance: IPublicClientApplication;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 1000; // Start with 1 second
  private messageQueue: RagChatMessage[] = [];
  private isConnected = false;
  private isConnecting = false;
  private onMessageCallback: ((chunk: RagResponseChunk) => void) | null = null;
  private onErrorCallback: ((error: Error) => void) | null = null;
  private onConnectCallback: (() => void) | null = null;
  private onDisconnectCallback: (() => void) | null = null;
  private _isSilentlyStopped = false;

  public get isSilentlyStopped(): boolean { return this._isSilentlyStopped; }

  constructor(sessionId: string, msalInstance: IPublicClientApplication) {
    this.sessionId = sessionId;
    this.connectionId = `conn_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    this.msalInstance = msalInstance;
    
    logger.log(`Creating new RAG WebSocket service - Connection ID: ${this.connectionId}`);
  }

  /**
   * Prime the TLS session with the target WebSocket host by issuing a
   * lightweight HTTPS request first.
   *
   * When the server (or APIM in front of it) has "Negotiate Client Certificate"
   * enabled, the TLS handshake includes a CertificateRequest message.  Browsers
   * handle this transparently for fetch/XHR but may not surface the certificate
   * picker for a WebSocket upgrade.  A preceding HTTPS fetch to the same origin
   * causes the browser to complete TLS (and any cert selection) over HTTP, and
   * the subsequent wss:// connection reuses that session.
   *
   * All errors are swallowed so this never blocks the normal connection path.
   *
   * @param wsUrl - The WebSocket URL that will be opened (used to derive the HTTPS origin)
   * @param accessToken - Bearer token so the probe is not rejected with 401
   */
  private async primeTlsSession(wsUrl: string, accessToken: string): Promise<void> {
    try {
      // Convert wss:// → https:// and use just the origin (not the full path),
      // since the WS path only handles WebSocket upgrades and returns 404 for HTTP.
      const url = new URL(wsUrl.replace(/^wss:\/\//i, 'https://'));
      const httpsUrl = url.origin;
      logger.log('Priming TLS session for client-certificate negotiation:', httpsUrl);
      await fetch(httpsUrl, {
        method: 'HEAD',
        headers: { Authorization: `Bearer ${accessToken}` },
        redirect: 'manual',
        signal: AbortSignal.timeout(5000),
      });
      logger.log('TLS session primed successfully');
    } catch (err) {
      logger.warn('TLS priming request did not succeed (non-fatal):', err);
    }
  }

  /**
   * Get the access token for API requests
   */
  private async getAccessToken(): Promise<string> {
    const accounts = this.msalInstance.getAllAccounts();
    
    if (accounts.length === 0) {
      throw new Error('No authenticated account found');
    }

    const request = {
      scopes: [`${process.env.REACT_APP_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`],
      account: accounts[0],
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (silentError: any) {
      // Check if it's an interaction_in_progress error
      if (silentError?.errorCode === 'interaction_in_progress') {
        logger.error('Cannot acquire token: interaction already in progress');
        throw new Error('Authentication interaction already in progress. Please wait and try again.');
      }
      
      // For other errors, try popup
      try {
        const response = await this.msalInstance.acquireTokenPopup(request);
        return response.accessToken;
      } catch (popupError: any) {
        // If popup also fails with interaction_in_progress, surface a better error
        if (popupError?.errorCode === 'interaction_in_progress') {
          throw new Error('Authentication interaction already in progress. Please wait and try again.');
        }
        throw popupError;
      }
    }
  }

  /**
   * Connect to the WebSocket server
   */
  public async connect(): Promise<void> {
    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      logger.log('Connection already in progress, waiting...');
      return;
    }

    if (this.isConnected && this.websocket?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isConnecting = true;
    let accessToken: string = '';
    try {
      // Get access token before connecting
      accessToken = await this.getAccessToken();
      
      // Connect to APIM WebSocket endpoint for Everyday Insights (bearer token only - no subscription key)
      const wsBaseUrl = process.env.REACT_APP_RAG_APIM_WS_URL || 'wss://ai-np-apim-main.mmm.com';
      const wsUrl = `${wsBaseUrl}/everydayinsights-ws?token=${encodeURIComponent(accessToken)}`;
      
      logger.log(`Connecting to RAG WebSocket backend - URL: ${wsUrl.substring(0, 100)}...`);
      logger.log(`Access Token Length: ${accessToken.length} characters`);

      // Prime the TLS session before opening the WebSocket so the browser
      // can handle any client-certificate negotiation via a normal HTTPS flow.
      await this.primeTlsSession(wsUrl, accessToken);

      try {
        this.websocket = new WebSocket(wsUrl);
        logger.log('WebSocket object created successfully');
      } catch (wsError) {
        logger.error('Failed to create WebSocket object:', wsError);
        logger.error('WebSocket creation error details:', {
          message: (wsError as Error).message,
          name: (wsError as Error).name,
          stack: (wsError as Error).stack
        });
        throw wsError;
      }

      return new Promise((resolve, reject) => {
        if (!this.websocket) {
          reject(new Error('WebSocket creation failed'));
          return;
        }

        this.websocket.onopen = () => {
          // If stopStream was called while the socket was connecting, abort everything.
          if (this._isSilentlyStopped) {
            logger.log(`RAG WebSocket onopen after silentDisconnect — ignoring (Connection: ${this.connectionId})`);
            this.websocket?.close(1000, 'Stopped before open');
            this.websocket = null;
            this.isConnected = false;
            resolve(); // resolve so any awaiting sendMessage can exit cleanly
            return;
          }
          logger.log(`RAG WebSocket CONNECTED - Connection ID: ${this.connectionId}`);
          this.isConnected = true;
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          
          // Send any queued messages
          this.flushMessageQueue();
          
          if (this.onConnectCallback) {
            this.onConnectCallback();
          }
          
          resolve();
        };

        this.websocket.onmessage = (event) => {
          // Ignore messages if this service was silently stopped
          if (this._isSilentlyStopped) return;
          try {
            // ===== RAW DATA LOGGING - BEFORE ANY PROCESSING =====
            logger.log(`RAW WebSocket event.data (Connection: ${this.connectionId}):`, event.data);
            logger.log('RAW data type:', typeof event.data);
            logger.log('RAW data length:', event.data.length);
            logger.log('RAW timestamp:', new Date().toISOString());
            logger.log('==================================================');
            
            // Log raw message data before parsing
            logger.log('RAG WebSocket RAW message received:', event.data);
            logger.log('RAG WebSocket message size (bytes):', new Blob([event.data]).size);

            const chunk: RagResponseChunk = JSON.parse(event.data);
            logger.log('RAG WebSocket PARSED message:', chunk);
            
            if (chunk.type === 'chat_error' && this.onErrorCallback) {
              this.onErrorCallback(new Error(chunk.error || 'Unknown error from RAG service'));
            } else if (this.onMessageCallback) {
              this.onMessageCallback(chunk);
            }
          } catch (error) {
            logger.error('Failed to parse RAG WebSocket message:', error);
            if (this.onErrorCallback) {
              this.onErrorCallback(new Error('Failed to parse server response'));
            }
          }
        };

        this.websocket.onerror = (error) => {
          logger.error('RAG WebSocket error:', error);
          logger.error('RAG WebSocket ERROR - Event object:', JSON.stringify(error, null, 2));
          logger.error('RAG WebSocket ERROR - Error type:', error.type);
          logger.error('RAG WebSocket ERROR - Full Access Token:', accessToken);
          // Don't reject here - let onclose handle reconnection logic
          // Only propagate error through callback if we've exhausted reconnection attempts
        };

        this.websocket.onclose = (event) => {
          logger.log('RAG WebSocket closed - Code:', event.code, 'Reason:', event.reason);
          logger.log('RAG WebSocket CLOSE - Event details:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            type: event.type
          });
          this.isConnected = false;
          this.isConnecting = false;
          
          // Log token on abnormal closure
          if (event.code !== 1000) {
            logger.error('RAG WebSocket CLOSED ABNORMALLY - Close Code:', event.code);
            logger.error('RAG WebSocket CLOSED ABNORMALLY - Reason:', event.reason);
            logger.error('RAG WebSocket CLOSED ABNORMALLY - Full Access Token:', accessToken);
          }
          
          if (this.onDisconnectCallback) {
            this.onDisconnectCallback();
          }

          // Attempt to reconnect if not a normal closure
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
            logger.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
            
            setTimeout(() => {
              this.connect().catch(err => {
                logger.error('Reconnection failed:', err);
                logger.error('Reconnection FAILED - Error message:', err.message);
                logger.error('Reconnection FAILED - Error stack:', err.stack);
                logger.error('Reconnection FAILED - Full Access Token:', accessToken);
                // Only propagate error after all attempts exhausted
                if (this.reconnectAttempts >= this.maxReconnectAttempts && this.onErrorCallback) {
                  this.onErrorCallback(new Error('Failed to connect to RAG service after multiple attempts'));
                }
              });
            }, delay);
          } else if (event.code !== 1000 && this.onErrorCallback) {
            // All reconnection attempts exhausted
            logger.error('RAG WebSocket ALL RECONNECTION ATTEMPTS EXHAUSTED - Close Code:', event.code);
            logger.error('RAG WebSocket ALL RECONNECTION ATTEMPTS EXHAUSTED - Reason:', event.reason);
            logger.error('RAG WebSocket ALL RECONNECTION ATTEMPTS EXHAUSTED - Full Access Token:', accessToken);
            this.onErrorCallback(new Error('WebSocket connection failed after maximum reconnection attempts'));
          }
        };
      });
    } catch (error) {
      this.isConnecting = false;
      logger.error('Failed to get access token or create WebSocket:', error);
      logger.error('WebSocket CONNECTION FAILED - Error details:', {
        message: (error as Error).message,
        name: (error as Error).name,
        stack: (error as Error).stack
      });
      logger.error('WebSocket CONNECTION FAILED - Full Access Token:', accessToken);
      throw error;
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  public disconnect(): void {
    if (this.websocket) {
      logger.log(`Disconnecting RAG WebSocket - Connection ID: ${this.connectionId}`);
      this.websocket.close(1000, 'Client disconnecting');
      this.websocket = null;
      this.isConnected = false;
    }
  }

  /**
   * Disconnect without triggering reconnect logic.
   * Use this when the user explicitly stops a stream so the onclose handler
   * doesn't attempt to re-establish the connection.
   */
  public silentDisconnect(): void {
    // Clear all callbacks BEFORE closing so onclose/onerror don't fire reconnect logic
    this.onMessageCallback = null;
    this.onErrorCallback = null;
    this.onConnectCallback = null;
    this.onDisconnectCallback = null;
    this.reconnectAttempts = this.maxReconnectAttempts; // exhaust retries as extra guard
    this._isSilentlyStopped = true;
    if (this.websocket) {
      logger.log(`Silent-disconnecting RAG WebSocket - Connection ID: ${this.connectionId}`);
      this.websocket.close(1000, 'Client stopped stream');
      this.websocket = null;
      this.isConnected = false;
    }
  }

  /**
   * Send a chat message
   */
  public async sendMessage(message: string, userEmail: string): Promise<void> {
    const messageId = this.generateMessageId();
    const timestamp = new Date().toISOString();

    const ragMessage: RagChatMessage = {
      action: 'chat_message',
      message,
      messageId,
      timestamp,
      userId: userEmail,
      sessionId: this.sessionId,
    };

    // If not connected, queue the message
    if (!this.isConnected || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      logger.log('WebSocket not ready, queueing message');
      this.messageQueue.push(ragMessage);
      
      // Attempt to connect if not already connecting
      if (!this.websocket || this.websocket.readyState === WebSocket.CLOSED) {
        await this.connect();
      }
      return;
    }

    try {
      const payload = JSON.stringify(ragMessage);
      this.websocket.send(payload);
      logger.log('RAG message sent:', ragMessage);
    } catch (error) {
      logger.error('Failed to send RAG message:', error);
      // Queue the message for retry
      this.messageQueue.push(ragMessage);
      throw error;
    }
  }

  /**
   * Set callback for receiving message chunks
   */
  public onMessage(callback: (chunk: RagResponseChunk) => void): void {
    this.onMessageCallback = callback;
  }

  /**
   * Set callback for errors
   */
  public onError(callback: (error: Error) => void): void {
    this.onErrorCallback = callback;
  }

  /**
   * Set callback for connection established
   */
  public onConnect(callback: () => void): void {
    this.onConnectCallback = callback;
  }

  /**
   * Set callback for disconnection
   */
  public onDisconnect(callback: () => void): void {
    this.onDisconnectCallback = callback;
  }

  /**
   * Check if WebSocket is connected
   */
  public isWebSocketConnected(): boolean {
    return this.isConnected && this.websocket?.readyState === WebSocket.OPEN;
  }

  /**
   * Flush queued messages when connection is established
   */
  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0 && this.isConnected && this.websocket?.readyState === WebSocket.OPEN) {
      const message = this.messageQueue.shift();
      if (message) {
        try {
          const payload = JSON.stringify(message);
          this.websocket.send(payload);
          logger.log('Queued RAG message sent:', message);
        } catch (error) {
          logger.error('Failed to send queued message:', error);
          // Put it back at the front of the queue
          this.messageQueue.unshift(message);
          break;
        }
      }
    }
  }

  /**
   * Generate a unique message ID
   */
  private generateMessageId(): string {
    return `nav_req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }
}
